import { z } from 'zod';

export interface PlannerView {
  id: string; // UUID
  name: string;
  phone: string;
  transportationType: string;
  employmentType: string;
  hubspotId: string;
  misoPartnerId: string;
  homeAddress?: string | null;
}

/**
 * 서버 API 스펙에 맞는 필터 타입
 * 서버는 value를 String?, values를 List<String>?로 받음
 */
export interface PlannerSearchFilter {
  field: string;
  operator: string;
  value: string | null;
  values: string[] | null;
}

/**
 * 서버 API 스펙에 맞는 필터 그룹 타입
 */
export interface PlannerSearchFilterGroup {
  logic: 'AND' | 'OR';
  filters: readonly PlannerSearchFilter[];
}

export interface PlannerSearchQuery {
  filterGroups: PlannerSearchFilterGroup[];
  sortBy: 'name' | 'registeredAt' | 'id';
  sortOrder: 'ASC' | 'DESC';
  pageSize: number;
  cursor?: string;
}

export interface PlannerSearchView {
  items: PlannerView[];
  hasNext: boolean;
  nextCursor: string | null;
  count: number;
}

// ============================================================================
// Zod Schemas
// ============================================================================

/**
 * PlannerView 스키마
 */
export const plannerViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string(),
  transportationType: z.string(),
  employmentType: z.string(),
  hubspotId: z.string(),
  misoPartnerId: z.string(),
  homeAddress: z.string().nullable().optional(),
});

/**
 * PlannerSearchFilter 스키마
 */
export const plannerSearchFilterSchema = z.object({
  field: z.string(),
  operator: z.string(),
  value: z.string().nullable(),
  values: z.array(z.string()).nullable(),
});

/**
 * PlannerSearchFilterGroup 스키마
 */
export const plannerSearchFilterGroupSchema = z.object({
  logic: z.enum(['AND', 'OR']),
  filters: z.array(plannerSearchFilterSchema),
});

/**
 * PlannerSearchQuery 스키마
 */
export const plannerSearchQuerySchema = z.object({
  filterGroups: z.array(plannerSearchFilterGroupSchema),
  sortBy: z.enum(['name', 'registeredAt', 'id']),
  sortOrder: z.enum(['ASC', 'DESC']),
  pageSize: z.number().int().positive(),
  cursor: z.string().optional(),
});

/**
 * PlannerSearchView 스키마
 */
export const plannerSearchViewSchema = z.object({
  items: z.array(plannerViewSchema),
  hasNext: z.boolean(),
  nextCursor: z.string().nullable(),
  count: z.number().int().nonnegative(),
});

// ============================================================================
// Type Inference from Schemas
// ============================================================================

export type PlannerViewInput = z.infer<typeof plannerViewSchema>;
export type PlannerSearchFilterInput = z.infer<typeof plannerSearchFilterSchema>;
export type PlannerSearchFilterGroupInput = z.infer<typeof plannerSearchFilterGroupSchema>;
export type PlannerSearchQueryInput = z.infer<typeof plannerSearchQuerySchema>;
export type PlannerSearchViewInput = z.infer<typeof plannerSearchViewSchema>;
